import React from 'react';
import PropTypes from 'prop-types';
import { compose, lifecycle, mapProps, withProps, withState, withStateHandlers } from 'recompose';
import { connect } from 'react-redux';
import uniqWith from 'lodash/uniqWith';
import isEqual from 'lodash/isEqual';
import uniq from 'lodash/uniq';
import moment from 'moment';
import { List } from 'immutable';
import { reduxForm, formValueSelector } from 'redux-form/immutable';
import classNames from 'classnames';
import round from 'lodash/round';
import get from 'lodash/get';

import { withFetch, withCreate } from '../../../app/entities/Entity/containers';
import requireProps from '../../../app/lib/requireProps';
import { Endpoints } from '../../../app/lib/LeanLinkService';
import { timeplanUnitsByIds } from '../../../app/entities/TimeplanUnit/selectors';
import { bookingById, bookingsByIds } from '../../../app/entities/Booking/selectors';
import { DatesInput } from '../../../app/components/Form/DatesInput/DatesInput';
import TimeReporting from './components/TimeReporting';
import Button from '../../../app/components/Button';
import styles from './styles.css';
import Routes from '../../Router/Routes';
import TimeSummary from './components/TimeSummary';
import withNotifications from '../../../app/components/Notifications/withNotifications';
import expandedTimeplanUnits from '../../../app/entities/TimeplanUnit/expandedTimeplanUnits';
import withMe from '../../containers/withMe';
import minuteDuration from '../../../app/components/Form/Timeplan/minuteDuration';
import Modal from '../../../app/components/Modal';
import LoadingScreen from '../../../app/LoadingScreen/LoadingScreen';
import { useTranslation, withTranslation } from 'react-i18next';

const TimeReportCalendar = compose(
  withTranslation(),
  withStateHandlers({ isOpen: true }, {
    toggleOpen: ({ isOpen }) => () => ({ isOpen: !isOpen }),
  }),
)(({ setDates, highlightedDates, dates, isOpen, toggleOpen, t }) => {
  if (!isOpen) {
    return (
      <Button
        onClick={toggleOpen}
        icon="calendar"
        variant="primary"
        stretch
        className={styles.calendarBtn}
      >
        {t("Visa kalender")}
      </Button>
    );
  }

  return (
    <div className={styles.calendarBox}>
      {/*
      <Button
        onClick={toggleOpen}
        icon="calendar"
        variant="primary"
        stretch
        className={styles.calendarBtn}
      >
        Dölj kalender
      </Button>
      */}
      <span>{t("Välj dagar att hantera")}</span>
      <span className="pull-right">
        <div><span className="btn btn-warning" style={{ backgroundColor: 'rgba(33, 150, 243, 0.27)' }} /> ={t(" Valda dagar")}</div>
        <div><span className="btn btn-warning" /> = {t("Bokade dagar")}</div>
      </span>
      <DatesInput
        input={{
          value: dates,
          onChange: setDates,
          onBlur: setDates,
        }}
        selectWeekDays={false}
        highlighted={highlightedDates}
        isHighlightedInfo
        hideRepeat
        hideInput
        hideClear
        hideToggleSelectWeekDays
        daysWeekTitle={t("dag...")}
        public
        noBottomMargin
        activePast
        clearPanel
      />
    </div>
  );
});

const EmptyTimeReports = compose(
  withMe,
  requireProps(['resource'], true, true),
  mapProps(({ resource }) => ({
    firstName: resource ? resource.get('firstName') : '',
    t: useTranslation().t,
  })),
)(({ firstName, t }) => (
  <div className="row">
    <div className="col-md-4 col-md-offset-4">
      <h3 data-testid="thanksText">{t("Tack")} {firstName}!</h3>
      <h4>{t("Alla dina tider är rapporterade")}</h4>
      {/* Should we display the full menu? */}
      <Button
        link={Routes.reportedTimes()}
        icon="calendar"
        variant="primary"
        stretch
      >
        {t("Visa rapporterade tider")}
      </Button>
      <Button
        link={Routes.home()}
        icon="home"
        variant="primary"
        stretch
      >
        {t("Gå till hemskärmen")}
      </Button>
    </div>
  </div>
));

EmptyTimeReports.propTypes = {
  firstName: PropTypes.string.isRequired,
};

const ConfirmModal = compose(
  withTranslation(),
  withProps(({ setShow }) => ({
    hideModal: () => setShow(false),
  })),
  connect((state, { form }) => ({
    timeplanUnits: formValueSelector(form)(state, 'timeplanUnits'),
    mileageAllowances: formValueSelector(form)(state, 'mileageAllowances'),
  })),
  withNotifications,
  lifecycle({
    componentDidMount() {
      const timeplanUnits = this.props.timeplanUnits;
      const tpuDates = uniq(timeplanUnits.map(({ date }) => date).toJS());
      if (tpuDates.length !== this.props.dates.size) {
        this.props.addError({ message: this.props.t('Vänligen fyll i minst ett arbetspass på samtliga dagar du vill rapportera') });
        this.props.hideModal();
      }
    },
  }),
  withProps(({ timeplanUnits, dates, mileageAllowances }) => {
    const periods = expandedTimeplanUnits(
      timeplanUnits
        .map(({ from, to, date, workType, booking, overtimeMinutes }) =>
          ({ from, to, date, workType, booking, overtimeMinutes }))
        .filter(({ date }) => dates.includes(date))
        .toJS(),
    );
    const hours = round(
      periods.reduce((acc, tpu) => (acc + minuteDuration(tpu)), 0) / 60,
      2,
    );
    const overtimeHours = round(
      periods.reduce((acc, { overtimeMinutes }) => (acc + overtimeMinutes), 0) / 60,
      2,
    );
    const totalKilometers = mileageAllowances.map(o => parseInt(o.get('kilometers'), 10))
      .reduce((acc, km) => acc + km, 0);
    return {
      hours,
      totalKilometers,
      overtimeHours,
    };
  }),
  withProps(({ hours, totalKilometers, mileageAllowances, overtimeHours, t }) => {
    if (overtimeHours > 0) {
      return {
        message: mileageAllowances.size === 0 ?
          `${t("Du rapporterar nu totalt")} ${hours} ${t("arbetade timmar inklusive")} ${overtimeHours} ${t("timmar övertid, vill du skicka in detta")}?` :
          `${t("Du rapporterar nu totalt")} ${hours} ${t("arbetade timmar inklusive")} ${overtimeHours} ${t("timmar övertid och")} ${totalKilometers} ${t("km milersättning, vill du skicka in detta")}?`,
      };
    }
    return {
      message: mileageAllowances.size === 0 ?
        `${t("Du rapporterar nu totalt")} ${hours} ${t("arbetade timmar, vill du skicka in detta")}?` :
        `${t("Du rapporterar nu totalt")} ${hours} ${t("arbetade timmar och")} ${totalKilometers} ${t("km milersättning, vill du skicka in detta")}?`,
    };
  }),
)(({ setShow, show, message, handleSubmit, hideModal, t }) => (
  <Modal
    title={t("Skicka in tidrapport")}
    setShow={setShow}
    show={show}
    size="small"
  >
    <span>
      {message}
    </span>
    <div style={{ marginTop: '20px' }}>
      <Button
        onClick={hideModal}
        icon="times-circle"
        size="lg"
        testID="noBtn"
      >
        {t("Nej")}
      </Button>
      <Button
        onClick={handleSubmit}
        variant="success"
        icon="check-circle"
        size="lg"
        testID="yesBtn"
        className="pull-right"
      >
        {t("Ja")}
      </Button>
    </div>
  </Modal>
));

function TimeReport({
  dates,
  setDates,
  initialDates,
  highlightedDates,
  handleSubmit,
  form,
  timeplanUnits,
  setShow,
  showModal,
  show,
  isLoading,
}) {
  const { t } = useTranslation();
  if (highlightedDates.size === 0) {
    return (
      <EmptyTimeReports />
    );
  }

  return (
    <div>
      <div className="row">
        <LoadingScreen isLoading={isLoading} position="fixed" />
        <div className="col-md-4 col-lg-3">
          <h3 className={styles.header}>{t("Tidrapportering")}</h3>
          <TimeReportCalendar
            dates={dates}
            initialDates={initialDates}
            highlightedDates={highlightedDates}
            setDates={setDates}
          />
        </div>
        <div className={`col-md-8 col-lg-9 ${styles.timereportBox}`}>
          <h3 className={classNames('visible-md visible-lg', styles.header)}>&nbsp;</h3>
          <TimeReporting dates={dates} form={form} timeplanUnits={timeplanUnits} />
          {/*
          <Button
            link={Routes.reportedTimes()}
            icon="calendar"
            variant="primary"
            stretch
          >
            Tidigare rapporterade tider
          </Button>
          */}
        </div>
        <div className={styles.confirmBox}>
          <TimeSummary form={form} compareTimeplanUnits={timeplanUnits} />
          <Button
            onClick={showModal}
            variant="success"
            icon="check-circle"
            size="lg"
            testID="sendTimeReport"
            stretch
          >
            {t("Skicka in tidrapport")}
          </Button>
        </div>
        <ConfirmModal
          handleSubmit={handleSubmit}
          setShow={setShow}
          show={show}
          form={form}
          dates={dates}
        />
      </div>
    </div>
  );
}

TimeReport.propTypes = {
  dates: PropTypes.instanceOf(List).isRequired,
  setDates: PropTypes.func.isRequired,
  initialDates: PropTypes.instanceOf(List).isRequired,
  highlightedDates: PropTypes.instanceOf(List).isRequired,
  handleSubmit: PropTypes.func.isRequired,
  form: PropTypes.string.isRequired,
  isLoading: PropTypes.bool.isRequired,
  show: PropTypes.bool.isRequired,
  setShow: PropTypes.func.isRequired,
  showModal: PropTypes.func.isRequired,
};

const withUnreportedTimeplanUnits = compose(
  withFetch(),
  lifecycle({
    componentWillMount() {
      const from = this.props.from;
      const to = this.props.to;
      const refreshTimereport = () => {
        this.props.fetch({ url: Endpoints.public.resourceApi.bookedPeriods({ from, to }) })
          .then((data) => {
            const timeplanUnitIds = data.entities.timeplanUnits ?
              Object.keys(data.entities.timeplanUnits) : [];
            this.setState({ timeplanUnitIds });
          });
        this.props.fetch({ url: Endpoints.public.resourceApi.reportedPeriods({ from, to }) })
          .then((data) => {
            const reportedTimeplanUnitsIds = data.entities.timeplanUnits ?
              Object.keys(data.entities.timeplanUnits) : [];
            this.setState({ reportedTimeplanUnitsIds });
          });
        this.props.fetch({ url: Endpoints.public.resourceApi.attestedPeriods({ from, to }) })
          .then((data) => {
            const attestedTimeplanUnitsIds = data.entities.timeplanUnits ?
              Object.keys(data.entities.timeplanUnits) : [];
            this.setState({ attestedTimeplanUnitsIds });
          });
      };

      refreshTimereport();
      this.setState({ refreshTimereport });
    },
  }),
  requireProps(['timeplanUnitIds', 'reportedTimeplanUnitsIds', 'attestedTimeplanUnitsIds'], true, true),
  connect((state, { reportedTimeplanUnitsIds, attestedTimeplanUnitsIds }) => {
    const reportedTPUs = timeplanUnitsByIds(state, reportedTimeplanUnitsIds);
    const reportedDatesAndCustomers = reportedTPUs.map(({ date, booking }) => {
      const customerId = bookingById(state, booking).customer;
      return {
        date,
        customerId,
      };
    });

    const attestedTPUs = timeplanUnitsByIds(state, attestedTimeplanUnitsIds);
    const attestedDatesAndCustomers = attestedTPUs.map(({ date, booking }) => {
      const customerId = bookingById(state, booking).customer;
      return {
        date,
        customerId,
      };
    });
    return {
      reportedDatesAndCustomers,
      attestedDatesAndCustomers,
    };
  }),
  withProps(({ reportedDatesAndCustomers, attestedDatesAndCustomers }) => ({
    reportedDatesAndCustomers: uniqWith(reportedDatesAndCustomers, isEqual),
    attestedDatesAndCustomers: uniqWith(attestedDatesAndCustomers, isEqual),
  })),
  connect((state, { timeplanUnitIds }) => {
    const timeplanUnits = timeplanUnitsByIds(state, timeplanUnitIds)
      .map((tpu) => {
        const booking = bookingById(state, tpu.get('booking'));
        const hasOvertimePrices = !!(booking && booking.bookingResource && booking.bookingResource.get('hasOvertimePrices'));
        return tpu
          .set('customer', booking.customer)
          .set('hasOvertimePrices', hasOvertimePrices);
      }).filter((tpu) => {
        const booking = bookingById(state, tpu.get('booking'));
        if (booking && booking.bookingResource && booking.bookingResource.has('publicTimeReport')) {
          return booking.bookingResource.get('publicTimeReport');
        }
        return true;
      });
    return {
      timeplanUnits,
    };
  }),
  mapProps(({ timeplanUnits, reportedDatesAndCustomers, attestedDatesAndCustomers, ...props }) => ({
    ...props,
    timeplanUnits: timeplanUnits.filter(({ date, customer }) => {
      const obj = { date, customerId: customer };
      const isReportedOrAttested = reportedDatesAndCustomers.some(e => isEqual(e, obj)) ||
        attestedDatesAndCustomers.some(e => isEqual(e, obj));
      return !isReportedOrAttested;
    }),
  })),
);

const uniqueCustomersFromTPUsOnDates = (tpus, dates) => uniq(tpus
  .filter(tpu => dates.contains(tpu.get('date')))
  .map(tpu => tpu.get('customer')),
);

const withDefaultMileageAllowances = connect((state, { timeplanUnits, dates }) => {
  const customerIds = uniqueCustomersFromTPUsOnDates(timeplanUnits, dates);
  const bookingIds = uniq(timeplanUnits
    .filter(tpu => dates.contains(tpu.get('date')))
    .map(tpu => tpu.get('booking')),
  );
  const bookings = bookingsByIds(state, bookingIds);
  const customerIdsWithMileageAllowance = customerIds.map((customerId) => {
    const bookingsOnCustomer = bookings.filter(o => o.customer === customerId);
    const mileageAllowanceEnabled = bookingsOnCustomer
      .find((booking) => {
        const bookingResource = get(booking, 'bookingResource');
        if (!bookingResource) {
          return false;
        }
        return bookingResource.get('mileageAllowanceEnabled') === true;
      });
    if (mileageAllowanceEnabled) {
      return customerId;
    }
    return null;
  }).filter(o => !!o);
  const defaultMileageAllowences = customerIdsWithMileageAllowance
    .map(customerId => ({ customer: customerId, kilometers: 0 }));
  return {
    defaultMileageAllowences,
  };
});

export default compose(
  withTranslation(),
  withNotifications,
  withProps({
    from: moment().add(-3, 'month').format('YYYY-MM-DD'),
    to: moment().format('YYYY-MM-DD'),
  }),
  withUnreportedTimeplanUnits,
  withProps(({ timeplanUnits }) => ({
    highlightedDates: new List(uniq(timeplanUnits.map(({ date }) => date))),
  })),
  withProps(({ highlightedDates }) => ({
    initialDates: highlightedDates.filter(date => moment().diff(date, 'days') !== 0),
  })),
  withState(
    'dates',
    'setDates',
    ({ initialDates }) => initialDates,
  ),
  withStateHandlers({ show: false }, {
    setShow: () => show => ({ show }),
    showModal: () => () => ({ show: true }),
  }),
  withCreate(({
    props: ({ create, ...props }) => ({
      ...props,
      onSubmit: (values, dispatch, reduxProps) => {
        reduxProps.setShow(false);
        const comments = values.get('comments');
        const mileageAllowances = values.get('mileageAllowances');
        const timeplanUnits = values.get('timeplanUnits');

        const periods = expandedTimeplanUnits(
          timeplanUnits
            .map(({ from, to, date, workType, booking, overtimeMinutes }) =>
              ({ from, to, date, workType, booking, overtimeMinutes }))
            .filter(({ date }) => props.dates.includes(date))
            .toJS(),
        );

        create({
          url: Endpoints.public.resourceApi.timeReports(),
          entity: {
            periods,
            comments,
            mileageAllowances,
          },
        })
          .then(() => {
            reduxProps.reset();
            props.refreshTimereport();
            props.setDates(new List());
            props.addSuccess({ message: props.t('Tack, tidrapporten är nu skickad till din konsultchef') });
          });
      },
    }),
  })),
  withProps(({ timeplanUnits, dates }) => {
    const defaultComments = uniqueCustomersFromTPUsOnDates(timeplanUnits, dates)
      .map(customerId => ({ customer: customerId, comment: '' }));
    return {
      defaultComments,
      timeplanUnits: timeplanUnits.filter(tpu => dates.contains(tpu.get('date'))),
    };
  }),
  withDefaultMileageAllowances,
  withProps(({ timeplanUnits, defaultComments, defaultMileageAllowences }) => ({
    initialValues: {
      timeplanUnits,
      comments: defaultComments,
      mileageAllowances: defaultMileageAllowences,
    },
  })),
  reduxForm({
    form: 'resource_time_report',
    enableReinitialize: true,
    keepDirtyOnReinitialize: false,
  }),
)(TimeReport);
